import React, { useState, useEffect } from 'react';
import UserModal from '../../components/User/User';
import { apiFetch, getApiBase, buildHeaders } from '../../lib/api';
import './UsersPage.css';

const API_BASE = getApiBase();

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('All');
  const [counts, setCounts] = useState({ all: 0, active: 0, inactive: 0 });
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState({});

  // --- FETCH FROM BACKEND ---
  const fetchUsers = async () => {
    try {
      const data = await apiFetch('/api/users/');
      setUsers(data);
      const active = data.filter(u => u.is_active !== false).length;
      const inactive = data.length - active;
      setCounts({ all: data.length, active, inactive });
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // --- MODAL HANDLERS ---
  const openCreateModal = () => {
    setSelectedUser({});
    setModalMode('create');
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // --- DATABASE ACTIONS ---
  const handleSaveUser = async (formData) => {
    if (!formData.full_name?.trim() || !formData.email?.trim() || !formData.phone?.trim()) {
      alert('Full name, email, and phone are required');
      return;
    }

    if (modalMode === 'create') {
      const response = await fetch(`${API_BASE}/api/users/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildHeaders(),
        },
        body: JSON.stringify({
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.detail || 'Failed to create user');
        return;
      }

      if (!result.email_sent) {
        alert('User created, but email was not sent. Configure Resend settings in the backend environment to enable delivery.');
      }
    } else {
      const response = await fetch(`${API_BASE}/api/users/${formData.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...buildHeaders(),
        },
        body: JSON.stringify({
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        alert(result.detail || 'Failed to update user');
        return;
      }
    }
    
    setIsModalOpen(false);
    fetchUsers(); // Refresh the table
  };

  const handleToggleActive = async (formData) => {
    const response = await fetch(`${API_BASE}/api/users/${formData.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...buildHeaders(),
      },
      body: JSON.stringify({ is_active: !formData.is_active }),
    });

    if (!response.ok) {
      const result = await response.json();
      alert(result.detail || 'Failed to update user status');
      return;
    }
    
    setIsModalOpen(false);
    fetchUsers(); // Refresh the table
  };

  // --- RENDER LOGIC ---
  const displayedUsers = users.filter(user => {
    const isActive = user.is_active !== false;
    if (filter === 'Active' && !isActive) return false;
    if (filter === 'Inactive' && isActive) return false;
    return true;
  });

  return (
    <div className="users-page-wrapper">
      
      <div className="users-header">
        <div className="users-title">
          <h1>Users</h1>
          <p>Manage user accounts and access permissions</p>
        </div>
        <button className="create-user-btn" onClick={openCreateModal}>
          + Create user
        </button>
      </div>

      <div className="users-filters">
        <button className={filter === 'All' ? 'active' : ''} onClick={() => setFilter('All')}>All ({counts.all})</button>
        <button className={filter === 'Active' ? 'active' : ''} onClick={() => setFilter('Active')}>Active ({counts.active})</button>
        <button className={filter === 'Inactive' ? 'active' : ''} onClick={() => setFilter('Inactive')}>Inactive ({counts.inactive})</button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingUsers ? (
              <tr>
                <td colSpan="5" className="users-loading-state">
                  <span className="table-loading-spinner" aria-hidden="true" />
                  Loading users...
                </td>
              </tr>
            ) : displayedUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="users-empty-state">
                  No users found for this filter.
                </td>
              </tr>
            ) : displayedUsers.map(user => {
              const isActive = user.is_active !== false;
              
              return (
                <tr key={user.id}>
                  <td className="user-name-cell">
                    <strong>{user.full_name || user.name || 'Unnamed User'}</strong>
                    <span>{user.phone || 'No phone'}</span>
                  </td>
                  <td className="user-email-cell">{user.email}</td>
                  
                  {/* DYNAMIC ROLE BADGE */}
                  <td>
                    <span className={`type-badge ${user.role === 'admin' ? 'type-executive' : 'type-operator'}`}>
                      {user.role === 'admin' ? 'Admin' : 'Operator'}
                    </span>
                  </td>
                  
                  {/* DYNAMIC STATUS BADGE */}
                  <td>
                    <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  
                  <td>
                    <span className="edit-action" onClick={() => openEditModal(user)}>Edit</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <UserModal 
        isOpen={isModalOpen}
        mode={modalMode}
        initialData={selectedUser}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveUser}
        onToggleActive={handleToggleActive}
      />

    </div>
  );
};

export default UsersPage;
